// Client-side HTML sanitization for admin-authored legal-document markup
// (TCX-788). The Terms / Privacy content is sanitized server-side at publish
// time, but that same HTML is injected via dangerouslySetInnerHTML on the
// PUBLIC Terms/Privacy pages and the admin preview. This is a defense-in-depth
// fallback: a server-side sanitizer bypass or regression must not become stored
// XSS to unauthenticated visitors. Every dangerouslySetInnerHTML sink for legal
// content routes through here so the config stays consistent and testable.
import DOMPurify, { type Config } from 'dompurify';

import {
  LEGAL_ALLOWED_ATTRIBUTES,
  LEGAL_ALLOWED_ATTRIBUTE_UNION,
  LEGAL_ALLOWED_SCHEMES,
  LEGAL_ALLOWED_TAGS,
  LEGAL_ANCHOR_REL,
  LEGAL_ANCHOR_TARGET,
} from '@legal-html-contract';

// Every list below is derived from the shared contract (TCX-823) in
// packages/nestJs/src/modules/legal-documents/legal-html.contract.ts — the
// same module the server-side sanitizer (legal-documents.sanitize.ts) builds
// its config from, so the two sanitizers cannot drift apart.
// We do NOT lean on DOMPurify's broad default profile — those defaults still
// admit markup the backend contract forbids (inline style="", <style>/<iframe>,
// data-* attributes, non-editor tags), so in the server-sanitizer-regression
// scenario this helper defends against, a stored payload could otherwise still
// render phishing/tracking/defacement HTML on the PUBLIC Terms/Privacy pages
// even though <script> is stripped. Restricting to the same contract closes
// that gap while preserving every tag/attribute legitimate legal formatting
// uses.

// Contract allowedTags (Tiptap output: headings, paragraphs, marks, lists,
// links, blockquote, tables).
const ALLOWED_TAGS = [...LEGAL_ALLOWED_TAGS];

// The contract's attribute allowlist is per-tag (a: href/target/rel; th/td:
// colspan/rowspan). DOMPurify's ALLOWED_ATTR is a flat GLOBAL list — it keeps
// any listed attribute on ANY element — so on its own it is broader than the
// backend: a stored `<a colspan="2">` or `<td href="…" target="…">` would keep
// the misapplied attribute here even though the backend's per-tag sanitize-html
// config drops it. We therefore pass the union as ALLOWED_ATTR AND register the
// enforceLegalPerTagAttributes hook below, which re-applies the contract's
// per-tag map after DOMPurify's pass so the FE mirror is byte-for-byte as
// strict as the backend (TCX-823 review: per-tag enforcement, not just the
// flat union).
//
// ALLOW_DATA_ATTR / ALLOW_ARIA_ATTR default to true in DOMPurify and are NOT
// gated by ALLOWED_ATTR — with them on, every data-*/aria-* attribute survives
// (verified: a data-track tracking attribute leaked through). The contract
// allowlist has no data-* or aria-*, so we turn both off to keep the mirror
// exact and deny tracking/behavioural hooks.
const ALLOWED_ATTR = [...LEGAL_ALLOWED_ATTRIBUTE_UNION];

// Contract allowedSchemes: http, https, mailto (nothing else). DOMPurify's
// default IS_ALLOWED_URI additionally admits tel/callto/sms/cid/xmpp/matrix, so
// we override it with a regexp BUILT from the contract's scheme list. The
// relative/fragment alternatives (`[^a-z]` and the `[a-z+.-]+(?:[^a-z+.-:]|$)`
// no-scheme branch) are copied from DOMPurify's default so relative links and
// #anchors still work; only the explicit scheme list is narrowed. Equivalent
// to the previous hand-written literal
// /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
// (`http|https` and `https?` are equivalent alternations).
const ALLOWED_URI_REGEXP = new RegExp(
  '^(?:(?:' +
    LEGAL_ALLOWED_SCHEMES.join('|') +
    '):|[^a-z]|[a-z+.\\-]+(?:[^a-z+.\\-:]|$))',
  'i',
);

// Exported for the vitest spec (sanitizeHtml.spec.ts), which asserts the
// derived pieces against the contract without calling DOMPurify.sanitize
// (see the happy-dom note above the sanitizeHtml export).
export const SANITIZE_CONFIG: Config = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  ALLOWED_URI_REGEXP,
  // Not covered by ALLOWED_ATTR; both default true and must be disabled to
  // strip data-*/aria-* (the contract allows neither).
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
};

// The backend transform (legal-documents.sanitize.ts) forces EVERY anchor to
// target="_blank" + rel="noopener noreferrer" (LEGAL_ANCHOR_TARGET /
// LEGAL_ANCHOR_REL in the contract) via simpleTransform, overwriting whatever
// target/rel the input carried. `target` and `rel` are both in ALLOWED_ATTR,
// so without this a stored anchor such as `<a target="legal" rel="opener">`
// would survive sanitization and open a NAMED browsing context that retains
// window.opener — reverse tabnabbing on the public legal pages. Mirror the
// backend exactly: on every <a>, unconditionally set the contract's
// target/rel, overwriting any attacker-supplied values rather than
// conditioning on the incoming target. Composed into enforceLegalAttributeContract
// below (the single hook registered once at module load, not per call, so it is
// not stacked on repeated sanitize() calls).
// Exported (rather than inlined into addHook) so the anchor transform is
// directly unit-testable in happy-dom — creating an element and running the
// hook doesn't touch DOMPurify's node-removal path (see the test note below).
export const enforceLegalAnchorAttributes = (node: Element): void => {
  if (node.nodeName === 'A') {
    node.setAttribute('target', LEGAL_ANCHOR_TARGET);
    node.setAttribute('rel', LEGAL_ANCHOR_REL);
  }
};

// Per-tag attribute enforcement — the structural mirror of the backend's
// per-tag sanitize-html `allowedAttributes` map. Because DOMPurify's
// ALLOWED_ATTR is a single flat GLOBAL union, an attribute allowed for one tag
// (href/target/rel for <a>, colspan/rowspan for <th>/<td>) would otherwise
// survive on the WRONG tag — e.g. `colspan` on an anchor or `href` on a <td>.
// After DOMPurify's own attribute pass, drop every attribute not allowed for
// THIS node's tag by the shared LEGAL_ALLOWED_ATTRIBUTES map. Tags with no
// entry in the map (headings, p, br, marks, lists, blockquote, table/thead/
// tbody/tr) are allowed zero attributes, exactly like the backend.
// Exported (like the anchor hook) so it is directly unit-testable on happy-dom
// elements without entering DOMPurify's node-removal path.
const legalAttributesForTag = (tag: string): readonly string[] =>
  (LEGAL_ALLOWED_ATTRIBUTES as Record<string, readonly string[]>)[tag] ?? [];

export const enforceLegalPerTagAttributes = (node: Element): void => {
  const allowed = legalAttributesForTag(node.nodeName.toLowerCase());
  // Iterate a static copy: removeAttribute mutates the live attributes list.
  for (const { name } of Array.from(node.attributes)) {
    if (!allowed.includes(name)) {
      node.removeAttribute(name);
    }
  }
};

// Single afterSanitizeAttributes hook = per-tag strip THEN anchor target/rel
// force. Order is safe: target/rel are allowed for <a>, so the strip keeps
// them, and the force then guarantees the contract's safe values regardless of
// what the input carried. Two focused pure functions, composed here so there is
// exactly one registration (asserted by sanitizeHtml.wiring.spec.ts).
export const enforceLegalAttributeContract = (node: Element): void => {
  enforceLegalPerTagAttributes(node);
  enforceLegalAnchorAttributes(node);
};

DOMPurify.addHook('afterSanitizeAttributes', enforceLegalAttributeContract);

// DOMPurify.sanitize itself is not unit-tested under vitest: the test env is
// happy-dom (see vitest.config.ts) and happy-dom 12.10.3's node-removal
// semantics are incompatible with DOMPurify 3.4.11's _forceRemove guard —
// sanitizing ANY payload that requires dropping a node throws "a node selected
// for removal could not be detached". jsdom is not installed. What IS covered
// by specs: the config derivation from the shared contract
// (sanitizeHtml.spec.ts), the anchor hook's behavior on real happy-dom
// elements (ditto), and that sanitizeHtml() actually routes through
// DOMPurify.sanitize with SANITIZE_CONFIG and registers the hook exactly once
// (sanitizeHtml.wiring.spec.ts, with dompurify mocked).
// Full sanitize behavior was verified in a real Chromium DOM:
// legitimate legal markup (headings, lists, paragraphs, bold/italic, tables,
// anchors with href) survives; out-of-contract markup DOMPurify's defaults
// would otherwise keep — <style>/<iframe>, inline style="", data-* attributes —
// is now stripped; <script>, onerror/onclick and javascript: hrefs stay
// stripped; a misapplied contract attribute on the wrong tag (`<a colspan="2">`,
// `<td href="…" target="_blank">`) is stripped by the per-tag hook so the FE is
// as strict per-tag as the backend; and every anchor — including one carrying a
// named target such as target="legal" rel="opener", and one with no target at
// all — is forced to target="_blank" rel="noopener noreferrer" (AC1/AC2 + this
// fix).
export const sanitizeHtml = (html?: string): string =>
  DOMPurify.sanitize(html ?? '', SANITIZE_CONFIG);

export default sanitizeHtml;
